import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarClock, LuChevronRight, LuMail, LuPhone, LuSearchX } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import { getInitials } from '../../../components/ui/Avatar';
import { useTranslation } from '../../../i18n/LanguageContext';
import { isEmptyValue } from '../employeeColumns';

const SKELETON_CARDS = 6;

const rowBackground = (selected, flashing) => {
  if (flashing) return 'tw-bg-amber-100 dark:tw-bg-[#433d2d]';
  if (selected) return 'tw-bg-teal-50 tw-shadow-[inset_3px_0_0_#14b8a6] dark:tw-bg-[#20383d]';
  return 'tw-bg-transparent active:tw-bg-slate-900/[0.04] dark:active:tw-bg-white/[0.05]';
};

const text = (value) => (isEmptyValue(value) ? '' : String(value));

/**
 * The employee list on narrow screens (phones, or a tablet next to the
 * sidebar): one tappable row per employee with the details that matter on
 * the go. Tapping opens the employee panel, which holds everything else.
 */
const EmployeeCardList = ({ rows, selectedId, flashId, onSelect, pendingLeaveCounts, isLoading, isBusy, canClearFilters, onClearFilters }) => {
  const { t } = useTranslation();

  if (isLoading && rows.length === 0) {
    return (
      <ul aria-label={t('employees.table.loading')} className="tw-divide-y tw-divide-slate-100 tw-border-t tw-border-slate-200/70 dark:tw-divide-white/[0.06] dark:tw-border-white/10">
        {Array.from({ length: SKELETON_CARDS }, (_, index) => (
          <li key={index} className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-4">
            <span className="tw-h-10 tw-w-10 tw-shrink-0 tw-animate-pulse tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10" />
            <span className="tw-flex-1 tw-space-y-2">
              <span className="tw-block tw-h-3 tw-w-2/5 tw-animate-pulse tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10" />
              <span className="tw-block tw-h-3 tw-w-3/5 tw-animate-pulse tw-rounded-full tw-bg-slate-200/60 dark:tw-bg-white/[0.07]" />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-border-t tw-border-slate-200/70 tw-px-6 tw-py-12 tw-text-center dark:tw-border-white/10">
        <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
          <LuSearchX aria-hidden="true" className="tw-h-7 tw-w-7" />
        </span>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('employees.table.empty')}</p>
        {canClearFilters && <Button size="sm" onClick={onClearFilters}>{t('employees.table.clearFilters')}</Button>}
      </div>
    );
  }

  return (
    <ul
      aria-label={t('employees.table.label')}
      className={`tw-divide-y tw-divide-slate-100 tw-border-t tw-border-slate-200/70 tw-transition-opacity tw-duration-200 dark:tw-divide-white/[0.06] dark:tw-border-white/10 ${isBusy ? 'tw-opacity-60' : 'tw-opacity-100'}`}
    >
      {rows.map((employee) => {
        const selected = employee.id === selectedId;
        const isActive = employee.Active === 1;
        const pending = pendingLeaveCounts[employee.id] || 0;
        const name = `${text(employee.eName)} ${text(employee.eSurname)}`.trim() || '-';
        const role = [text(employee.departman), text(employee.profession)].filter(Boolean).join(' · ');
        const phone = text(employee.phone);
        const email = text(employee.eMail);

        return (
          <li key={employee.id} className="tw-block">
            <button
              type="button"
              data-employee-id={employee.id}
              aria-pressed={selected}
              onClick={() => onSelect(employee)}
              className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-left tw-transition-colors tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/60 ${rowBackground(selected, employee.id === flashId)}`}
            >
              <span
                aria-hidden="true"
                className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
              >
                {getInitials(employee.eName, employee.eSurname)}
              </span>

              <span className="tw-min-w-0 tw-flex-1">
                <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                  <span className="tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{name}</span>
                  {pending > 0 && (
                    <span
                      title={t('employees.table.pendingLeave', { count: pending })}
                      className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-0.5 tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-amber-700 dark:tw-text-amber-300"
                    >
                      <LuCalendarClock aria-hidden="true" className="tw-h-3 tw-w-3" />
                      {pending}
                      <span className="tw-sr-only"> {t('employees.table.pendingLeaveSr')}</span>
                    </span>
                  )}
                </span>
                {role && <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">{role}</span>}
                {(phone || email) && (
                  <span className="tw-mt-1.5 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    {phone && (
                      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums">
                        <LuPhone aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                        {phone}
                      </span>
                    )}
                    {email && (
                      <span className="tw-inline-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1">
                        <LuMail aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                        <span className="tw-truncate">{email}</span>
                      </span>
                    )}
                  </span>
                )}
              </span>

              <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                <Badge tone={isActive ? 'success' : 'neutral'}>{isActive ? t('employees.status.active') : t('employees.status.inactive')}</Badge>
                <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-300 dark:tw-text-slate-600" />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

EmployeeCardList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedId: PropTypes.string,
  flashId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  pendingLeaveCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  isLoading: PropTypes.bool,
  isBusy: PropTypes.bool,
  canClearFilters: PropTypes.bool,
  onClearFilters: PropTypes.func.isRequired,
};

export default EmployeeCardList;
